import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as mock1Data from "./toefl-mock1/speakingData";
import * as mock7Data from "./toefl-mock7/speakingData";
import {
  C,
  SubmittingScreen,
  usePageTop,
  useWindowWidth,
  useVoicesReady,
  scrollToTop,
  speakLines,
  speechSupported,
  estimateSpeechSeconds,
  linearBand,
  bandSummary,
  saveToeflResult,
} from "./toeflShared";

// ============================================================
// TOEFL iBT SPEAKING (2026 format)
// Question sets live in src/quiz/toefl-mock<N>/speakingData.js
// Part 1: Listen and Repeat (7 sentences)
// Part 2: Take an Interview (4 questions, 45 seconds each)
//
// Prompts are synthesized with text-to-speech; answers are transcribed
// with the browser's SpeechRecognition API (Chrome / Edge) and scored
// from the transcript.
// ============================================================

const SPEAKING_DATA = { 1: mock1Data, 7: mock7Data };

const getRecognitionClass = () =>
  typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

const roundHalf = (n) => Math.round(n * 2) / 2;
const clamp5 = (n) => Math.min(5, Math.max(0, n));
const normalizeWords = (text) => (text.toLowerCase().replace(/[’']/g, "'").match(/[a-z0-9']+/g) || []);

// Longest common subsequence of two word lists (word-order-aware match).
function lcs(a, b) {
  const dp = Array(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i++) {
    let prev = 0;
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev + 1 : Math.max(dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[b.length];
}

function scoreRepeat(target, said) {
  const t = normalizeWords(target);
  const s = normalizeWords(said);
  if (s.length === 0) return { score: 0, accuracy: 0 };
  const accuracy = lcs(t, s) / t.length;
  const extra = Math.max(0, s.length - t.length) / t.length; // added words
  const adjusted = accuracy - extra * 0.5;
  const score =
    adjusted >= 0.95 ? 5 : adjusted >= 0.85 ? 4 : adjusted >= 0.7 ? 3 : adjusted >= 0.5 ? 2 : adjusted > 0.2 ? 1 : 0;
  return { score, accuracy: Math.round(accuracy * 100) };
}

const SUPPORT_WORDS = ["because", "for example", "for instance", "so", "also", "but", "however", "i think", "i believe", "in my opinion", "such as", "since", "and then", "first", "another"];

function scoreInterview(question, said) {
  const words = normalizeWords(said);
  const wc = words.length;
  const feedback = [];
  if (wc === 0) return { score: 0, feedback: ["No speech was detected."] };

  let score = wc >= 80 ? 5 : wc >= 60 ? 4.5 : wc >= 45 ? 4 : wc >= 30 ? 3 : wc >= 15 ? 2 : 1;
  if (wc < 60) feedback.push(`${wc} words in 45 seconds — try to keep talking for the full time.`);

  const relevant = new Set(words.filter((w) => question.keywords.includes(w))).size;
  if (relevant < 2) {
    score -= 1;
    feedback.push("Answer the question more directly.");
  }

  const lower = ` ${words.join(" ")} `;
  const support = SUPPORT_WORDS.filter((p) => lower.includes(` ${p} `)).length;
  if (support === 0) {
    score -= 0.5;
    feedback.push("Explain your ideas with reasons or examples (\"because\", \"for example\").");
  }

  const content = words.filter((w) => w.length > 3);
  const ttr = content.length ? new Set(content).size / content.length : 0;
  if (content.length >= 10 && ttr < 0.45) {
    score -= 0.5;
    feedback.push("Vary your vocabulary — some words are repeated a lot.");
  }

  // Two-part questions: each part needs at least one signal word.
  if (question.parts) {
    const missed = question.parts.filter((part) => !part.some((p) => lower.includes(` ${p} `))).length;
    if (missed > 0) {
      score -= missed === question.parts.length ? 2 : 1;
      feedback.push(missed === question.parts.length
        ? "The answer does not address what the question asks."
        : "The question has two parts — your answer covers only one of them.");
    }
  }

  if (feedback.length === 0) feedback.push("Relevant, well-developed answer.");
  return { score: clamp5(roundHalf(score)), feedback };
}

// Continuous recognizer that restarts itself if the browser ends the
// session early (Chrome stops after a pause in speech).
function startRecognizer(onText, onError) {
  const Recognition = getRecognitionClass();
  const rec = new Recognition();
  rec.lang = "en-US";
  rec.continuous = true;
  rec.interimResults = true;
  let finalText = "";
  let active = true;

  rec.onresult = (event) => {
    let interim = "";
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      if (result.isFinal) finalText += `${result[0].transcript} `;
      else interim += result[0].transcript;
    }
    onText(`${finalText}${interim}`.trim());
  };
  rec.onerror = (event) => {
    if (event.error === "not-allowed" || event.error === "service-not-allowed" || event.error === "audio-capture") {
      active = false;
      onError(event.error);
    }
  };
  rec.onend = () => {
    if (active) {
      try {
        rec.start();
      } catch (e) {
        /* already started */
      }
    }
  };
  rec.start();

  return () => {
    active = false;
    try {
      rec.stop();
    } catch (e) {
      /* not running */
    }
  };
}

const repeatSeconds = (sentence) => Math.max(8, Math.ceil(sentence.split(/\s+/).length * 0.9) + 5);

function ToeflSpeaking({ mock = 1 }) {
  const { listenRepeat, interview } = SPEAKING_DATA[mock] || mock1Data;
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width < 768;
  usePageTop();
  useVoicesReady();

  const recognitionSupported = !!getRecognitionClass() && speechSupported();

  // stage: "intro" | "repeat" | "interview" | "submitting"
  const [stage, setStage] = useState("intro");
  const [index, setIndex] = useState(0);
  // phase: "context" | "listening" | "recording" | "done"
  const [phase, setPhase] = useState("context");
  const [liveText, setLiveText] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [micError, setMicError] = useState(null);
  const [responses, setResponses] = useState({ repeat: [], interview: [] });

  const liveTextRef = useRef("");
  const stopRecRef = useRef(() => {});
  const cancelSpeechRef = useRef(() => {});
  const timersRef = useRef([]);
  const submittedRef = useRef(false);

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearInterval(t));
    timersRef.current = [];
  };

  const stopEverything = useCallback(() => {
    clearTimers();
    stopRecRef.current();
    cancelSpeechRef.current();
  }, []);

  useEffect(() => stopEverything, [stopEverything]);

  useEffect(() => {
    scrollToTop();
  }, [stage]);

  const prompts = stage === "repeat" ? listenRepeat.sentences : interview.questions.map((q) => q.text);

  // ── Recording ─────────────────────────────────────────────────
  const finishRecording = useCallback(() => {
    clearTimers();
    stopRecRef.current();
    stopRecRef.current = () => {};
    const text = liveTextRef.current;
    setResponses((prev) => {
      const list = [...prev[stage]];
      list[index] = text;
      return { ...prev, [stage]: list };
    });
    setPhase("done");
  }, [stage, index]);

  const finishRef = useRef(finishRecording);
  finishRef.current = finishRecording;

  const startRecording = (seconds) => {
    liveTextRef.current = "";
    setLiveText("");
    setSecondsLeft(seconds);
    setPhase("recording");
    stopRecRef.current = startRecognizer(
      (text) => {
        liveTextRef.current = text;
        setLiveText(text);
      },
      (err) => {
        setMicError(err);
        finishRef.current();
      }
    );
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          setTimeout(() => finishRef.current(), 0);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    timersRef.current.push(id);
  };

  // Plays the prompt, then starts recording automatically.
  const playPrompt = (stageName, i) => {
    const text = stageName === "repeat" ? listenRepeat.sentences[i] : interview.questions[i].text;
    const seconds = stageName === "repeat" ? repeatSeconds(text) : interview.responseSeconds;
    const lines = [{ speaker: "Speaker", gender: stageName === "repeat" ? listenRepeat.speakerGender : interview.speakerGender, text }];
    setPhase("listening");
    let started = false;
    const begin = () => {
      if (started) return;
      started = true;
      clearTimers();
      startRecording(seconds);
    };
    cancelSpeechRef.current = speakLines(lines, { rate: 0.92, onEnd: begin });
    // Fallback in case the browser never reports the end of speech
    const fallback = setInterval(() => {
      clearInterval(fallback);
      begin();
    }, estimateSpeechSeconds(lines) * 1000);
    timersRef.current.push(fallback);
  };

  // ── Scoring & submission ──────────────────────────────────────
  const submitTest = async (finalResponses) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    stopEverything();
    setStage("submitting");

    const review = [];
    const repeatScores = listenRepeat.sentences.map((sentence, i) => {
      const said = finalResponses.repeat[i] || "";
      const { score, accuracy } = scoreRepeat(sentence, said);
      review.push({
        number: review.length + 1,
        category: "Listen and Repeat",
        questionText: `Repeat: "${sentence}"`,
        userAnswerText: said || "No response",
        correctAnswerText: sentence,
        isCorrect: score >= 4,
        explanation: `Score ${score}/5 — ${accuracy}% of the words matched in order.`,
      });
      return score;
    });
    const interviewScores = interview.questions.map((q, i) => {
      const said = finalResponses.interview[i] || "";
      const { score, feedback } = scoreInterview(q, said);
      review.push({
        number: review.length + 1,
        category: "Take an Interview",
        questionText: q.text,
        userAnswerText: said || "No response",
        correctAnswerText: null,
        isCorrect: score >= 3,
        explanation: `Score ${score}/5 — ${feedback.join(" ")}`,
      });
      return score;
    });

    const avg = (list) => list.reduce((a, b) => a + b, 0) / list.length;
    const repeatAvg = Math.round(avg(repeatScores) * 10) / 10;
    const interviewAvg = Math.round(avg(interviewScores) * 10) / 10;
    const summary = bandSummary(linearBand(0.5 * (repeatAvg / 5) + 0.5 * (interviewAvg / 5)));

    const taskScores = [
      {
        label: "Listen and Repeat",
        score: repeatAvg,
        max: 5,
        diagnostics: [
          `${repeatScores.filter((s) => s >= 4).length} of ${repeatScores.length} sentences repeated accurately.`,
          repeatAvg < 4
            ? "Practise shadowing: repeat short audio clips word for word, focusing on small words like articles and prepositions."
            : "Accurate repetition — keep working on natural rhythm and stress.",
        ],
      },
      {
        label: "Take an Interview",
        score: interviewAvg,
        max: 5,
        diagnostics: [
          interviewAvg < 4
            ? "Use the full 45 seconds: give your answer, a reason, and a specific example."
            : "Well-developed answers — keep adding specific personal examples.",
        ],
      },
    ];

    const saveStatus = await saveToeflResult("TOEFL Speaking", {
      format: "TOEFL iBT 2026",
      mock,
      ...summary,
      listenRepeat: repeatAvg,
      interview: interviewAvg,
      breakdown: review,
    });

    navigate("/quiz/toefl/result", {
      state: { testType: "Speaking", ...summary, taskScores, review, saveStatus },
    });
  };

  // Exit ends the section early: responses so far (including one being
  // recorded now) are scored and saved; unanswered prompts score zero.
  const handleExit = () => {
    if (!window.confirm("Exit the Speaking test? Your responses so far will be scored and saved. Prompts you have not answered will score zero.")) return;
    const final = { repeat: [...responses.repeat], interview: [...responses.interview] };
    if (phase === "recording") final[stage][index] = liveTextRef.current;
    submitTest(final);
  };

  const handleNext = () => {
    const last = index === prompts.length - 1;
    if (!last) {
      const next = index + 1;
      setIndex(next);
      playPrompt(stage, next);
    } else if (stage === "repeat") {
      setStage("interview");
      setIndex(0);
      setPhase("context");
    } else {
      submitTest(responses);
    }
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  if (stage === "submitting") return <SubmittingScreen isMobile={isMobile} message="Scoring your speaking..." />;

  const page = { maxWidth: "820px", margin: "0 auto", padding: isMobile ? "92px 14px 40px" : "120px 24px 40px", color: "#111" };
  const card = { background: "#fff", border: `1px solid ${C.border}`, borderRadius: "18px", boxShadow: "0 6px 22px rgba(12,87,46,0.08)", padding: isMobile ? "20px 16px" : "30px 34px" };

  if (stage === "intro") {
    return (
      <div style={page}>
        <div style={card}>
          <div style={eyebrow}>TOEFL iBT • Speaking</div>
          <h1 style={{ color: C.greenText, margin: "0 0 10px", fontSize: isMobile ? "1.5rem" : "1.9rem" }}>Speaking Section</h1>
          <p style={{ color: C.muted, lineHeight: 1.7, marginTop: 0 }}>
            About 8 minutes. Your answers are recorded through your microphone and transcribed automatically.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", margin: "14px 0 18px", fontSize: "0.92rem" }}>
            <tbody>
              <tr style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={td}><strong>Listen and Repeat</strong></td>
                <td style={td}>Hear {listenRepeat.sentences.length} sentences once each and repeat them exactly.</td>
              </tr>
              <tr style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
                <td style={td}><strong>Take an Interview</strong></td>
                <td style={td}>Answer {interview.questions.length} questions. Recording starts right after each question, and you have {interview.responseSeconds} seconds.</td>
              </tr>
            </tbody>
          </table>
          <ul style={{ color: "#444", lineHeight: 1.8, paddingLeft: "1.2rem", fontSize: "0.92rem" }}>
            <li>Use a quiet room and allow microphone access when your browser asks.</li>
            <li>Each prompt plays once. Recording begins automatically after it ends.</li>
            <li>Speak clearly at a natural pace. You can press "Done" to stop early.</li>
          </ul>

          {!recognitionSupported && (
            <div style={warnBox}>
              Speech recognition is not available in this browser. Please use Google Chrome or Microsoft Edge on a
              computer, or book an in-centre speaking assessment instead.
            </div>
          )}

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "18px" }}>
            <button
              type="button"
              disabled={!recognitionSupported}
              onClick={() => {
                setStage("repeat");
                setIndex(0);
                setPhase("context");
              }}
              style={{ ...primaryBtn, opacity: recognitionSupported ? 1 : 0.5, cursor: recognitionSupported ? "pointer" : "not-allowed" }}
            >
              Start Speaking Test →
            </button>
            <button type="button" onClick={() => navigate("/quiz/toefl/speaking/appointment")} style={ghostBtn}>
              Book an in-centre assessment
            </button>
          </div>
        </div>
      </div>
    );
  }

  const partNumber = stage === "repeat" ? 1 : 2;
  const partTitle = stage === "repeat" ? "Listen and Repeat" : "Take an Interview";
  const context = stage === "repeat" ? listenRepeat.context : interview.context;

  if (phase === "context") {
    return (
      <div style={page}>
        <div style={card}>
          <div style={eyebrow}>Part {partNumber} of 2</div>
          <h2 style={{ color: C.greenText, margin: "0 0 10px" }}>{partTitle}</h2>
          <p style={{ lineHeight: 1.75, color: "#333" }}>{context}</p>
          <button type="button" onClick={() => playPrompt(stage, 0)} style={primaryBtn}>
            Begin Part {partNumber} →
          </button>
        </div>
      </div>
    );
  }

  const isLast = index === prompts.length - 1;

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px", marginBottom: "6px" }}>
          <h1 style={{ color: C.green, fontSize: isMobile ? "1.15rem" : "1.5rem", margin: 0 }}>TOEFL Speaking</h1>
          <span style={{ color: C.accent, fontWeight: 600, fontSize: "0.9rem" }}>
            Part {partNumber}: {partTitle} · {index + 1} of {prompts.length}
          </span>
        </div>
        <div style={{ height: "4px", background: "#e0f0e8", borderRadius: "2px", marginBottom: "20px" }}>
          <div style={{ height: "100%", width: `${((index + 1) / prompts.length) * 100}%`, background: C.accent, transition: "width 0.3s" }} />
        </div>

        {/* PROMPT */}
        <div style={{ background: C.softer, border: `1px solid ${C.border}`, borderRadius: "14px", padding: isMobile ? "16px" : "22px 24px", textAlign: "center", marginBottom: "16px" }}>
          {phase === "listening" && (
            <>
              <div style={{ fontSize: "2rem" }}>🔊</div>
              <p style={{ margin: "6px 0 0", color: C.greenText, fontWeight: 600 }}>
                {stage === "repeat" ? "Listen carefully..." : "The interviewer is speaking..."}
              </p>
            </>
          )}
          {phase === "recording" && (
            <>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "10px", color: C.red, fontWeight: 700 }}>
                <span style={{ width: "12px", height: "12px", borderRadius: "50%", background: C.red, animation: "tx-pulse 1s infinite" }} />
                Recording · {secondsLeft}s
              </div>
              <style>{"@keyframes tx-pulse { 50% { opacity: 0.3; } }"}</style>
              <p style={{ margin: "8px 0 0", color: C.muted, fontSize: "0.9rem" }}>
                {stage === "repeat" ? "Repeat the sentence now." : "Answer the question now."}
              </p>
            </>
          )}
          {phase === "done" && (
            <p style={{ margin: 0, color: C.greenText, fontWeight: 600 }}>✓ Response recorded</p>
          )}
          {/* The interview question stays on screen, as in the real test; Listen
              and Repeat sentences are audio-only until answered. */}
          {stage === "interview" && phase !== "listening" && (
            <p style={{ margin: "12px 0 0", fontSize: "0.95rem", lineHeight: 1.6, color: "#333" }}>{prompts[index]}</p>
          )}
        </div>

        {/* LIVE TRANSCRIPT */}
        {(phase === "recording" || phase === "done") && (
          <div style={{ border: `1px dashed ${C.border}`, borderRadius: "12px", padding: "12px 16px", minHeight: "60px", fontSize: "0.95rem", lineHeight: 1.7, color: liveText ? "#222" : "#aaa", marginBottom: "16px" }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: C.muted, marginBottom: "4px" }}>
              What we heard
            </div>
            {liveText || (phase === "recording" ? "Start speaking..." : "No speech detected.")}
          </div>
        )}

        {micError && (
          <div style={warnBox}>
            Microphone access was blocked ({micError}). Allow microphone access for this site in your browser settings, then
            restart the test.
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
          <button type="button" onClick={handleExit} style={ghostBtn}>
            Exit Test
          </button>
          {phase === "recording" && (
            <button type="button" onClick={finishRecording} style={primaryBtn}>
              Done
            </button>
          )}
          {phase === "done" && (
            <button type="button" onClick={handleNext} style={primaryBtn}>
              {!isLast ? "Next →" : stage === "repeat" ? "Continue to Part 2 →" : "Finish & Submit ✓"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────
const eyebrow = {
  display: "inline-block",
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "1.6px",
  textTransform: "uppercase",
  color: C.green,
  border: `1px solid ${C.border}`,
  borderRadius: "50px",
  padding: "5px 14px",
  marginBottom: "14px",
};

const td = { padding: "10px 8px", verticalAlign: "top", lineHeight: 1.6 };

const warnBox = {
  background: "#fff6e5",
  border: "1px solid #f0cf8f",
  borderRadius: "10px",
  padding: "12px 14px",
  color: "#7a5208",
  fontSize: "0.88rem",
  lineHeight: 1.6,
  marginBottom: "14px",
};

const primaryBtn = {
  padding: "11px 28px",
  background: C.accent,
  color: "#fff",
  border: "none",
  borderRadius: "30px",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: "0.95rem",
};

const ghostBtn = {
  padding: "10px 20px",
  background: "transparent",
  border: `1px solid ${C.border}`,
  color: "#555",
  borderRadius: "30px",
  cursor: "pointer",
  fontSize: "0.9rem",
};

export default ToeflSpeaking;
